import { Injectable } from '@angular/core';
import { EnvironmentService } from './environment/environment.service';

const PROBE_TIMEOUT_MS = 8000;
const OVERLAY_ID = 'le-deployment-wait-overlay';
const OVERLAY_STYLE_ID = 'le-deployment-wait-overlay-style';
const SPINNER_CLASS = 'le-deployment-wait-spinner';

/**
 * Watches for the app's API hosts becoming unreachable - the signature of a deploy/recycle window
 * (the browser reports it as a CORS preflight failure, but the real cause is the gateway dropping
 * requests while the app restarts). While any request is holding for the outage, a fixed overlay is
 * shown in the top-left of the window so the user knows to wait rather than refresh.
 *
 * The DOM element is managed directly (not through an Angular component) so it can appear even when
 * the failure happens during application bootstrap, before the root component has rendered.
 */
@Injectable({ providedIn: 'root' })
export class DeploymentMonitorService {

  private _inflightProbe: Promise<boolean> | null = null;
  private _waitingCount = 0;
  private _overlayElement: HTMLElement | null = null;

  constructor(private readonly _environmentService: EnvironmentService) { }

  /**
   * Confirms the request failed because the API host is unreachable while the user's connection to
   * this app is still alive - i.e. the outage is on the server side (deploy/restart), not local
   * connectivity. Concurrent callers share one in-flight probe.
   */
  confirmApiOutage = (failedRequestUrl: string): Promise<boolean> => {
    this._inflightProbe ??= this.probe(failedRequestUrl)
      .finally(() => { this._inflightProbe = null; });
    return this._inflightProbe;
  };

  /** Marks a request as waiting for the outage to end; shows the overlay while at least one waits. */
  beginWait = (): void => {
    this._waitingCount++;
    this.showOverlay();
  };

  endWait = (): void => {
    this._waitingCount = Math.max(0, this._waitingCount - 1);
    if (this._waitingCount === 0) {
      this.hideOverlay();
    }
  };

  /**
   * A `fetch` to the failed host's `/api/diagnostics/version` health endpoint. A `fetch` rejects on
   * a network-level failure - a dropped connection or a CORS-blocked gateway error page, which is
   * how the browser surfaces the app-gateway/front-door response during a recycle. A resolved
   * response below 500 means the service is healthy; a 5xx (readiness failure during warmup, or a
   * gateway error that carried CORS headers through) means it is not.
   */
  private probe = async (failedRequestUrl: string): Promise<boolean> => {
    const apiDown = await this.fetchMayFail(this.healthProbeUrl(failedRequestUrl), 'cors')
      .then(response => response.status >= 500, () => true);
    if (!apiDown) {
      return false;
    }
    return this.fetchMayFail(this.originProbeUrl(), 'no-cors')
      .then(() => true, () => false);
  };

  private healthProbeUrl = (failedRequestUrl: string): string => {
    try {
      return `${new URL(failedRequestUrl).origin}/api/diagnostics/version`;
    } catch {
      return failedRequestUrl;
    }
  };

  private fetchMayFail = (url: string, mode: RequestMode): Promise<Response> =>
    fetch(url, {
      method: 'HEAD',
      mode,
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });

  private originProbeUrl = (): string =>
    `${location.origin}/favicon.ico?le-probe=${Date.now()}`;

  private overlayTitle = (): string => {
    const env = (this._environmentService.environment || '').toLowerCase();
    if (env.includes('pr')) {
      return 'Server maintenance in progress — hang tight';
    }
    if (env.includes('ut')) {
      return 'UAT deployment in progress — hang tight';
    }
    return 'DV deployment in progress — hang tight';
  };

  private showOverlay = (): void => {
    if (typeof document === 'undefined') {
      return;
    }
    if (!this._overlayElement) {
      this.ensureOverlayStyles();
      const overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.setAttribute('role', 'alert');
      overlay.style.cssText = [
        'position:fixed',
        'top:16px',
        'left:16px',
        'z-index:2147483647',
        'display:flex',
        'align-items:center',
        'gap:10px',
        'padding:10px 16px',
        'border-radius:8px',
        'background:#1f2937',
        'color:#f9fafb',
        'font-family:Roboto, "Helvetica Neue", sans-serif',
        'font-size:14px',
        'line-height:1.4',
        'box-shadow:0 4px 12px rgba(0,0,0,.35)',
        'pointer-events:none',
      ].join(';');

      const spinner = document.createElement('span');
      spinner.className = SPINNER_CLASS;
      spinner.style.cssText = [
        'width:16px',
        'height:16px',
        'flex:none',
        'border:2px solid rgba(249,250,251,.35)',
        'border-top-color:#f9fafb',
        'border-radius:50%',
        `animation:${SPINNER_CLASS} 0.8s linear infinite`,
      ].join(';');

      const text = document.createElement('span');
      const title = document.createElement('div');
      title.style.fontWeight = '600';
      title.textContent = this.overlayTitle();
      const subtitle = document.createElement('div');
      subtitle.style.cssText = 'font-size:12px;color:#d1d5db';
      subtitle.textContent = 'The server is restarting; requests will resume automatically.';
      text.append(title, subtitle);

      overlay.append(spinner, text);
      this._overlayElement = overlay;
    }
    if (!this._overlayElement.isConnected) {
      document.body.appendChild(this._overlayElement);
    }
  };

  private hideOverlay = (): void => {
    this._overlayElement?.remove();
  };

  private ensureOverlayStyles = (): void => {
    if (document.getElementById(OVERLAY_STYLE_ID)) {
      return;
    }
    const style = document.createElement('style');
    style.id = OVERLAY_STYLE_ID;
    style.textContent = `@keyframes ${SPINNER_CLASS} { to { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
  };
}
